import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Dumbbell,
  ShoppingCart,
  Check,
  Star,
  Package,
  Truck,
  Percent,
  Pause,
  Play,
  ArrowLeftRight,
  XCircle,
  Receipt,
  Calendar,
} from "lucide-react";

const formatKES = (n) => `KES ${Number(n || 0).toLocaleString()}`;

const BeFit = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [mySubscription, setMySubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, plansRes, mySubRes, historyRes] = await Promise.all([
        apiClient.get("/products?category=fit"),
        apiClient.get("/subscriptions/plans"),
        apiClient.get("/subscriptions/my").catch(() => ({ data: null })),
        apiClient.get("/subscriptions/history").catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data);
      setSubscriptionPlans(plansRes.data);
      setMySubscription(mySubRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (product) => {
    try {
      await apiClient.post("/cart/add", {
        product_id: product.product_id,
        quantity: 1,
        size: product.sizes?.[0] || null,
      });
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const subscribe = async (planType) => {
    setActionPending(true);
    try {
      const response = await apiClient.post("/subscriptions/subscribe", {
        plan_type: planType,
      });
      toast.success(
        `Subscribed to ${response.data.plan?.name || planType}! +${response.data.points_earned} points`
      );
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setActionPending(false);
    }
  };

  const pauseOrResume = async () => {
    setActionPending(true);
    try {
      const isPaused = mySubscription?.status === "paused";
      const res = await apiClient.post(
        isPaused ? "/subscriptions/resume" : "/subscriptions/pause"
      );
      toast.success(res.data.message);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionPending(false);
    }
  };

  const changePlan = async (planType) => {
    setActionPending(true);
    try {
      const res = await apiClient.post("/subscriptions/change-plan", {
        plan_type: planType,
      });
      toast.success(res.data.message);
      setChangeOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setActionPending(false);
    }
  };

  const cancelSubscription = async () => {
    setActionPending(true);
    try {
      await apiClient.post("/subscriptions/cancel");
      toast.success("Subscription cancelled");
      setCancelOpen(false);
      await fetchData();
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setActionPending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const myPlan = mySubscription?.plan;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="be-fit-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">BE Fit</h1>
          </div>
          <p className="text-muted-foreground">
            Premium supplements and nutrition products for your wellness journey.
          </p>
        </motion.div>

        <Tabs defaultValue="products" className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="subscriptions-tab">BE Fit Box</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {products.map((product, index) => (
                <motion.div
                  key={product.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/product/${product.product_id}`}
                    className="group block product-card"
                    data-testid={`fit-product-${product.product_id}`}
                  >
                    <div className="relative aspect-square rounded-2xl overflow-hidden mb-4 bg-muted">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Button
                          size="sm"
                          className="rounded-full bg-secondary hover:bg-secondary/90"
                          onClick={(e) => {
                            e.preventDefault();
                            addToCart(product);
                          }}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Badge variant="outline" className="text-xs bg-secondary/10 text-secondary border-secondary/20">
                        {product.subcategory}
                      </Badge>
                      <h3 className="font-medium truncate">{product.name}</h3>
                      <p className="text-secondary font-heading font-bold text-lg">
                        {formatKES(product.price)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-8">
            {/* Current Subscription Card */}
            {mySubscription && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card
                  className="bg-secondary/5 border-secondary/20"
                  data-testid="current-subscription-card"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                          <Package className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <CardTitle className="font-heading text-xl flex items-center gap-2">
                            {myPlan?.name || mySubscription.plan_type.toUpperCase()}
                            <Badge
                              className={
                                mySubscription.status === "active"
                                  ? "bg-green-500/10 text-green-700 border-green-500/20"
                                  : "bg-amber-500/10 text-amber-700 border-amber-500/20"
                              }
                              variant="outline"
                            >
                              {mySubscription.status}
                            </Badge>
                          </CardTitle>
                          <CardDescription>
                            {formatKES(mySubscription.price)}/month • Next renewal{" "}
                            {new Date(mySubscription.next_renewal).toLocaleDateString()}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={pauseOrResume}
                          disabled={actionPending}
                          data-testid="pause-resume-btn"
                        >
                          {mySubscription.status === "paused" ? (
                            <>
                              <Play className="w-4 h-4 mr-2" /> Resume
                            </>
                          ) : (
                            <>
                              <Pause className="w-4 h-4 mr-2" /> Pause
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setChangeOpen(true)}
                          data-testid="change-plan-btn"
                        >
                          <ArrowLeftRight className="w-4 h-4 mr-2" />
                          Change Plan
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryOpen(true)}
                          data-testid="billing-history-btn"
                        >
                          <Receipt className="w-4 h-4 mr-2" />
                          History
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCancelOpen(true)}
                          data-testid="cancel-sub-btn"
                          className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {myPlan?.features && (
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {myPlan.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-secondary flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              </motion.div>
            )}

            {/* Subscription Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionPlans.map((plan, index) => {
                const isCurrent = mySubscription?.plan_type === plan.id;
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      className={`tier-card h-full flex flex-col ${
                        plan.id === "active" ? "popular ring-2 ring-secondary" : ""
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.id === "active" && (
                        <div className="bg-secondary text-white text-center py-1 text-sm font-medium">
                          Most Popular
                        </div>
                      )}
                      <CardHeader>
                        <CardTitle className="font-heading">{plan.name}</CardTitle>
                        <CardDescription>{plan.description}</CardDescription>
                        <div className="pt-4">
                          <span className="font-heading font-bold text-4xl">
                            {formatKES(plan.price)}
                          </span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        <div className="text-sm text-secondary font-medium">
                          +{plan.bonus_points} BE Points on subscribe
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1">
                        <ul className="space-y-3">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                      <CardFooter>
                        <Button
                          className={`w-full ${plan.id === "active" ? "bg-secondary hover:bg-secondary/90" : ""}`}
                          variant={plan.id === "active" ? "default" : "outline"}
                          onClick={() => (mySubscription ? changePlan(plan.id) : subscribe(plan.id))}
                          disabled={isCurrent || actionPending}
                          data-testid={`subscribe-${plan.id}-btn`}
                        >
                          {isCurrent ? "Current Plan" : mySubscription ? "Switch" : "Subscribe"}
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {[
                { icon: Truck, title: "Free Delivery", desc: "Direct to your door every month" },
                { icon: Percent, title: "Exclusive Discounts", desc: "Save on all store products" },
                { icon: Star, title: "Bonus Points", desc: "Earn up to 1,000 BE Points" },
              ].map((benefit, index) => (
                <Card key={index}>
                  <CardContent className="p-6 text-center">
                    <benefit.icon className="w-10 h-10 mx-auto mb-4 text-secondary" />
                    <h3 className="font-heading font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Billing History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Subscription History
            </DialogTitle>
            <DialogDescription>
              Every change to your BE Fit Box subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No subscription activity yet.
              </p>
            ) : (
              history.map((h) => (
                <div
                  key={h.history_id}
                  className="flex items-center justify-between p-3 bg-muted/40 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {h.action.replace("_", " ")} • {h.plan_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleString()}
                        {h.note ? ` · ${h.note}` : ""}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-mono">{formatKES(h.price)}</span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Change Plan</DialogTitle>
            <DialogDescription>
              Switch your BE Fit Box. Your next box ships on the standard schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {subscriptionPlans
              .filter((p) => p.id !== mySubscription?.plan_type)
              .map((p) => (
                <button
                  key={p.id}
                  onClick={() => changePlan(p.id)}
                  disabled={actionPending}
                  className="w-full text-left p-4 rounded-lg border border-border hover:border-secondary transition-colors disabled:opacity-50"
                  data-testid={`switch-to-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-sm text-muted-foreground">{p.description}</p>
                    </div>
                    <span className="font-heading font-bold text-lg">{formatKES(p.price)}</span>
                  </div>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Cancel BE Fit Box?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll lose access to your monthly box and bonus discounts. You can re-subscribe
              anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-keep">Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelSubscription}
              disabled={actionPending}
              data-testid="cancel-confirm"
              className="bg-destructive hover:bg-destructive/90"
            >
              Cancel Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default BeFit;
